import { useState } from "react";
import { useSelector } from "react-redux";

import {
  setBookingFormMarketplaceFromAcquisitionParams,
  setBookingFormValue,
} from "shared/data/actions/bookingForm";
import {
  setAdditionalServicePrice,
  setPaymentPrice,
} from "shared/data/actions/price";
import { setActualFlightServices } from "shared/data/actions/search";
import { AppState } from "shared/data/reducers";
import { getAdditionalServiceRefsWithoutMarketplace } from "shared/lib/getAdditionalServiceRefsWithoutMarketplace";
import { getCountedFullPrice } from "shared/lib/getCountedFullPrice";
import { fetchAcquisitionParams } from "shared/lib/golCopy";
import { isMobile } from "shared/lib/isMobile";
import { resolveRefs } from "shared/lib/resolveRefs";

import { deleteCookie, setCookie } from "../../data/cookiesOps";

const PROMO_CODE_WARNING_ERROR = "50-33";

const LOADING_PROMO_CODE_WARNING = "LOADING";

const isActualServiceRef = (ref) => typeof ref === "string" && ref.length > 0;

export function useResultPromoCodes({
  getPriceWithPromoCode,
  destination,
  dispatch,
  refs,
  alternativeCurrency,
}) {
  const [promoCodeWarning, setPromoCodeWarning] = useState(null);
  const [promoCodePopupErrorMessage, setPromoCodePopupErrorMessage] = useState(
    null
  );

  const {
    promoCodesEnabled,
    searchQuery,
    priceState,
    additionalServices,
    paymentTypeId,
  } = useSelector((state: AppState) => ({
    promoCodesEnabled: state.requestorConfig.promoCodesEnabled,
    searchQuery: state.storage.searchQuery,
    priceState: state.price,
    additionalServices: state.price.additionalServices,
    paymentTypeId: state.bookingForm.paymentTypeId,
  }));

  const applyPromoCode = async (promoCode = null) => {
    let filledPromoCode = null;
    if (isMobile() && promoCode) {
      filledPromoCode = promoCode;
    } else if (!isMobile() && typeof document !== "undefined") {
      filledPromoCode = document.getElementById(
        `result-promocodes-form-code-field_${destination}`
      )?.value;
    }

    setPromoCodeWarning({ Code: LOADING_PROMO_CODE_WARNING, $t: "test" });

    if (!filledPromoCode) {
      await dispatch(setBookingFormValue("promoCode", null));
      setPromoCodeWarning(null);
    }

    const resultSentPromoCode = await getPriceWithPromoCode(filledPromoCode);
    const isApplyDiscountSuccess =
      resultSentPromoCode?.promoCodeWarning?.Code !== PROMO_CODE_WARNING_ERROR;
    const actualDiscount = isApplyDiscountSuccess
      ? resultSentPromoCode?.discountPrice ?? 0
      : 0;

    const refsResolved = await resolveRefs(
      null,
      resultSentPromoCode.refs?.length > 0 ? resultSentPromoCode.refs : refs,
      priceState
    );

    const countedFullPrice = getCountedFullPrice({
      additionalServicePrice: null,
      additionalServices,
      priceState,
      injectedDiscount: actualDiscount,
    });

    const acquisitionData = await fetchAcquisitionParams({
      refs: refsResolved,
      fullPrice: countedFullPrice,
      defaultCountry: "cz",
      promoCode: filledPromoCode,
      alternativeCurrency,
    });

    const isMarketplace = acquisitionData?.bookingForm?.BookingFormGroup?.find(
      (el) => el?.Code === "marketplace"
    );

    if (isMarketplace) {
      const actualMarketplaceServices = [];
      const getActualMarketplaceServices = (marketplaceServices) => {
        actualMarketplaceServices.push(...marketplaceServices);
      };

      await dispatch(
        setBookingFormMarketplaceFromAcquisitionParams({
          acquisitionParamsBookingForm:
            acquisitionData?.bookingForm?.BookingFormGroup,
          getActualMarketplaceServices,
        })
      );

      if (actualMarketplaceServices.length !== 0) {
        actualMarketplaceServices?.forEach(async (el) => {
          if (el !== undefined) {
            await dispatch(
              setAdditionalServicePrice({
                name: el?.data?.name,
                price: el?.data?.price,
                serviceName: el?.data?.serviceName,
                serviceType: el?.data?.serviceType,
                marketplaceServiceName: el?.label,
                ref: el?.data?.ref,
              })
            );
          }
        });
      }

      const allRefsActualized = [
        ...actualMarketplaceServices
          .map((el) => el?.data?.ref)
          .filter(isActualServiceRef),
        ...getAdditionalServiceRefsWithoutMarketplace(additionalServices),
      ];

      await dispatch(setActualFlightServices(allRefsActualized));
    }

    const paymentFormMethod = acquisitionData?.paymentFormMethods?.find(
      (method) => method.Id === paymentTypeId
    );

    if (!isMobile()) {
      if (
        filledPromoCode &&
        resultSentPromoCode?.promoCodeWarning?.Code !== PROMO_CODE_WARNING_ERROR
      ) {
        setCookie({
          name: "promoCode",
          value: filledPromoCode,
          expiration: 3600 * 1000,
        });
      } else {
        deleteCookie("promoCode");
      }
    }

    if (paymentFormMethod) {
      await dispatch(setPaymentPrice(paymentFormMethod.Price || null));
    }

    await dispatch(
      setBookingFormValue(
        "ticketingCombinations",
        acquisitionData?.ticketingCombinations
      )
    );
    await dispatch(
      setBookingFormValue(
        "paymentFormMethods",
        acquisitionData?.paymentFormMethods
      )
    );

    setPromoCodeWarning(resultSentPromoCode.promoCodeWarning);
    setPromoCodePopupErrorMessage(
      resultSentPromoCode.promoCodePopupErrorMessage
    );
  };

  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    applyPromoCode();
  };

  const onSubmitMobile = (promoCode) => {
    applyPromoCode(promoCode);
  };

  return {
    promoCodesEnabled,
    onSubmit,
    onSubmitMobile,
    promoCodeWarning,
    promoCodePopupErrorMessage,
    setPromoCodePopupErrorMessage,
    isMultiCity: !isMobile()
      ? Object.keys(searchQuery).includes("flight_1_origin") &&
        Object.keys(searchQuery).includes("flight_1_destination")
      : searchQuery?.typeSearch === "MULTICITY",
  };
}
